import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, mergeMap, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { AuErrorService } from '@core/services/au-error-service';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { AuErrorAdapter } from '@app/shared';
import {
	addIncident,
	createIncidentError,
	createIncidentSuccess,
	deleteIncidents,
	deletingIncidentSuccess,
	editIncidentSuccess,
	incidentsFailedToLoad,
	incidentsLoaded,
	loadDefaultIncidents,
	openIncidentWorkspaceFromOutside,
	openNewIncidentWorkspaceFromOutside,
	startCreateIncident,
	startDeletingIncident,
	startEditIncident,
	startLoadingIncidents,
	updateIncident,
} from './incidents.actions';
import { IncidentsService } from '@app/pages/cmms/services/incidents.service';
import { incidentsQueryVariables } from '@app/pages/cmms/components/incident/state/incidents.selectors';
import { Store } from '@ngrx/store';
import { IIncidentState } from '@app/pages/cmms/components/incident/state/incidents.reducer';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { defaultPageSize } from '@app/pages/cmms/configs/pagination-query-variables';
import { TranslateService } from '@tolgee/ngx';

@Injectable()
export class IncidentsEffects {
	constructor(
		private actions$: Actions,
		private incidentsService: IncidentsService,
		private notify: IconSnackBarService,
		private sidebarService: AuRightSidebarService,
		private errorService: AuErrorService,
		private store$: Store<IIncidentState>,
		private translateService: TranslateService
	) {}

	incidentsOpened$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(loadDefaultIncidents),
			map(() => {
				return startLoadingIncidents({ variables: {} });
			})
		);
	});

	startLoadingIncidents$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startLoadingIncidents),
			map(({ variables }) => {
				if (variables?.first || variables?.last) {
					return variables;
				}
				return {
					...variables,
					first: defaultPageSize,
				};
			}),
			switchMap(variables => {
				return this.incidentsService.loadIncidents(variables).pipe(
					map(({ incidentPagination, incidents }) =>
						incidentsLoaded({ incidentPagination, incidents })
					),
					catchError(error => {
						this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
						return of(incidentsFailedToLoad());
					})
				);
			})
		);
	});

	startCreateIncident$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startCreateIncident),
			exhaustMap(({ variables, skipTableReload, closeSidebar, dexterSource }) => {
				return this.incidentsService.createIncidents(variables).pipe(
					map(({ success, incident }) => {
						if (success) {
							if (closeSidebar === undefined || closeSidebar === true) {
								this.sidebarService.closeAndClearPortal();
							}
							return createIncidentSuccess({ incident, skipTableReload, dexterSource });
						} else {
							return createIncidentError({});
						}
					}),
					catchError(error => {
						return of(createIncidentError({ error }));
					})
				);
			})
		);
	});

	createIncidentSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(createIncidentSuccess),
			withLatestFrom(this.store$.select(incidentsQueryVariables)),
			map(([incidentData, variables]) => {
				this.notify.success(
					`${this.translateService.instant('incident')} ${this.translateService.instant('has_been_created_successfully')}`
				);
				this.incidentsService.updateCalendarData$.next();

				return incidentData.skipTableReload
					? addIncident({ incident: incidentData.incident })
					: startLoadingIncidents({ variables });
			})
		);
	});

	createIncidentError$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(createIncidentError),
				map(({ error }) => {
					if (error) {
						this.errorService.notifyUserAboutError({
							error: error,
							defaultMessage: this.translateService.instant('error_generic_try_again'),
						});
					}
					this.notify.error(this.translateService.instant('error_generic_try_again'));
				})
			);
		},
		{ dispatch: false }
	);

	startEditIncident$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startEditIncident),
			switchMap(({ incident }) => {
				return this.incidentsService.editIncident(incident).pipe(
					map(({ incident }) => {
						this.notify.success(
							`${this.translateService.instant('incident')} ${this.translateService.instant('has_been_updated_successfully')}`
						);
						return editIncidentSuccess({ incident });
					}),
					catchError(error => {
						this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
						return EMPTY;
					})
				);
			})
		);
	});

	editIncidentSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(editIncidentSuccess),
			map(({ incident }) => {
				this.incidentsService.updateCalendarData$.next();
				return updateIncident({
					update: {
						// this is id of entity adapter!!! this code is correct;
						id: incident.pk,
						changes: incident,
					},
				});
			})
		);
	});

	startDeletingIncident$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startDeletingIncident),
			mergeMap(({ pk, closeSidebar }) => {
				return this.incidentsService.deleteIncident(pk).pipe(
					map(() => {
						if (closeSidebar === undefined || closeSidebar === true) {
							this.sidebarService.close();
						}
						return deletingIncidentSuccess({ pk });
					})
				);
			})
		);
	});

	deletingIncidentSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(deletingIncidentSuccess),
			withLatestFrom(this.store$.select(incidentsQueryVariables)),
			map(([_, variables]) => {
				this.notify.success(
					`${this.translateService.instant('incident')} ${this.translateService.instant('has_been_deleted_successfully')}`
				);
				this.incidentsService.updateCalendarData$.next();

				return startLoadingIncidents({ variables });
			})
		);
	});

	deleteMultipleIncidents$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(deleteIncidents),
			withLatestFrom(this.store$.select(incidentsQueryVariables)),
			mergeMap(([{ pks }, variables]) => {
				return this.incidentsService.deleteIncidents(pks).pipe(
					map(() => {
						const multiple = pks?.length > 1;
						this.notify.success(
							`${multiple ? pks?.length + ' ' : ''}Incident${multiple ? 's' : ''} ${
								multiple ? 'have' : 'has'
							} been deleted successfully`
						);
						return startLoadingIncidents({ variables });
					}),
					catchError(error => {
						this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
						return EMPTY;
					})
				);
			})
		);
	});

	openIncidentWorkspaceFromOutside$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(openIncidentWorkspaceFromOutside),
				exhaustMap(({ pk }) => {
					return this.incidentsService.getIncidentByPk(pk);
				}),
				map((incident: IIncident) => {
					// If Incident was deleted server will return null, so we check it and notify user
					if (!incident) {
						this.notify.warning(
							`${this.translateService.instant('incident')} ${this.translateService.instant('has_been_deleted')}`
						);
					} else {
						this.incidentsService.viewIncidentInWorkspace(incident);
					}
				}),
				catchError(error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
					return EMPTY;
				})
			);
		},
		{ dispatch: false }
	);

	opeNewIncidentWorkspaceFromOutside$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(openNewIncidentWorkspaceFromOutside),
				map(() => {
					return this.incidentsService.openNewIncidentWorkspace();
				})
			);
		},
		{ dispatch: false }
	);
}
